<template>
  <div>
    <div class="correct-order--additional-content">
      <p
        class="correct-order--guide-text"
        v-if="canShowDragDropStatement"
      >
        {{ $t('dragAndDropStatements') }}
      </p>
      <a
        v-if="canShowQuizResult"
        v-show="result.id && !result.correct || result.timeout"
        @click="showCorrectAnswer"
        class="correct-order--show-correct"
        :class="{'white': slide.textColour === '#ffffff'}"
      >
        <span v-if="!isShowingCorrectAnswer">{{ $t('seeCorrectAnswer') }}</span>
        <span v-else>
          {{ $t('seeYourAnswer') }}
        </span>
      </a>
    </div>
    <div class="correct-order--content">
      <div class="correct-order--indices">
        <div
          class="correct-order--index"
          v-for="(item, index) in correctOrderOptions"
          :key="item.id"
        >
          {{ index + 1 }}
        </div>
      </div>
      <draggable
        :disabled="isDisableDrag"
        class="correct-order--items"
        tag="div"
        v-model="correctOrderOptions"
        v-bind="dragOptions"
        @start="onStartDragging"
        @end="onStopDragging"
        @change="sortOption"
      >
        <transition-group
          class="correct-order--transition-group"
          :name="(result.id && result.pairs || result.timeout) && 'flip-list'"
          tag="ul"
        >
          <li
            class="correct-order--item"
            :class="{
              'draggable': !isDisableDrag,
              'is-dragging-dark': slide.textColour === '#ffffff',
              'is-dragging-light': slide.textColour === '#313131',
              'glass--black': slide.textColour === '#ffffff',
              'glass--white': slide.textColour === '#313131',
            }"
            v-for="(item, index) in correctOrderOptions"
            v-show="item.id"
            :key="item.id"
            :style="{borderColor: item.color}"
          >
            <div class="correct-order--item-text">
              {{ item.title }}
            </div>
            <div class="correct-order--drag-icon" v-show="!isDisableDrag">
              <i class="fas fa-grip-vertical" />
            </div>
            <div class="correct-order--drag-handler" v-if="!isDisableDrag" />
            <AhaSvg
              name="correct"
              view-box="0 0 18 18"
              maintain-color
              class="aha-option__icon icon-correct"
              v-show="isShowIcon"
              v-if="isItemInCorrectOrder(item, index)"
            />
            <AhaSvg
              name="incorrect"
              view-box="0 0 18 18"
              maintain-color
              class="aha-option__icon icon-incorrect"
              v-show="isShowIcon"
              v-if="!isItemInCorrectOrder(item, index)"
            />
          </li>
        </transition-group>
      </draggable>
    </div>
  </div>
</template>
<script>
import { mapGetters, mapState } from 'vuex'
import draggable from 'vuedraggable'
import { cloneDeep } from 'lodash'
import util from '@/util'
import QuizStatuses from '@/constants/QuizStatuses'

export default {
  components: {
    draggable,
  },
  props: {
    result: {
      type: Object,
      default: () => {},
    },
    isQuizSubmitted: {
      type: Boolean,
      default: false,
    },
    isWaitingQuizResult: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      isDragging: false,
      draggingIndex: null,
      correctOrderOptions: [],
      isShowingCorrectAnswer: false,
      QuizStatuses,
      submittedSlideOptions: [],
    }
  },
  computed: {
    ...mapGetters(['canShowQuizResult']),
    ...mapState({
      audienceId: (state) => state.audienceId,
      slide: (state) => state.slide,
      slideOptions: (state) => state.slide.SlideOptions,
      presentation: (state) => state.presentation,
    }),
    dragOptions() {
      return {
        animation: 200,
        group: 'description',
        disabled: false,
        delay: 0,
        ghostClass: 'ghost',
        chosenClass: 'choosen-item',
        handle: '.correct-order--drag-handler',
      }
    },
    isDisableDrag() {
      return this.canShowQuizResult || this.isQuizSubmitted || this.isWaitingQuizResult
    },
    isShowIcon() {
      return this.canShowQuizResult && !this.isShowingCorrectAnswer
    },
    canShowDragDropStatement() {
      return this.slide.quizStatus === QuizStatuses.QUESTION_SCREEN
    },
  },
  watch: {
    'result.id': function resultUpdated() {
      this.updateCorrectOrderOptionsIfResultExist()
    },
  },
  mounted() {
    if (this.slide.randomColours && this.slide.randomColours.length) {
      this.setOptionsColor()
    }
    this.correctOrderOptions = util.shuffleArray(cloneDeep(this.slideOptions))
      .filter((option) => option.id).map((option) => {
        return {
          id: option.id,
          title: option.title,
          order: option.order,
          color: option.color,
        }
      })
    this.$emit('updateCorrectOrderAnswer', this.correctOrderOptions)
    this.updateCorrectOrderOptionsIfResultExist()
  },
  methods: {
    isItemInCorrectOrder(item, index) {
      return this.slideOptions[index].id === item.id
    },

    setOptionsColor() {
      this.slideOptions.forEach((opt, optIndex) => {
        opt.color = this.slide.randomColours[optIndex]
      })
    },

    showCorrectAnswer() {
      if (this.isShowingCorrectAnswer) {
        this.isShowingCorrectAnswer = false
        this.correctOrderOptions = cloneDeep(this.submittedSlideOptions)
      } else {
        this.isShowingCorrectAnswer = true
        this.correctOrderOptions = cloneDeep(this.slideOptions)
      }
    },

    updateCorrectOrderOptionsIfResultExist() {
      if (this.result && this.result.id) {
        if (this.result.pairs) {
          this.submittedSlideOptions = this.result.pairs.map((slideOptionId) => {
            return this.slideOptions
              .find((opt) => opt.id === slideOptionId)
          })
        } else {
          // handle converting from other slide types
          // https://ahaslides.atlassian.net/browse/AHA-7649
          this.submittedSlideOptions = cloneDeep(this.correctOrderOptions)
        }
      }
      if (this.result && this.result.timeout) {
        this.submittedSlideOptions = cloneDeep(this.correctOrderOptions)
      }
      if (this.submittedSlideOptions.length) {
        this.correctOrderOptions = this.submittedSlideOptions
      }
    },

    onStartDragging(event) {
      this.draggingIndex = event.oldIndex
      this.isDragging = true
    },

    onStopDragging() {
      this.isDragging = true
      this.draggingIndex = null
    },

    async sortOption() {
      this.$emit('updateCorrectOrderAnswer', this.correctOrderOptions)
    },
  },
}
</script>

<style lang="scss" scoped>
@import "@/assets/scss/variables.scss";
$correct-order-item-height: 56px;
.correct-order-content {
  line-height: 1.4;
  text-align: left;
}

.glass--white {
  background: rgba(255, 255, 255, 0.8);
}

.glass--black {
  background: rgba(44, 44, 44, 0.8);
}

.correct-order--content {
  display: flex;
  flex-direction: row;
  margin-bottom: 16px;
}

.correct-order--additional-content {
  display: block;
  margin-bottom: 20px;
}

.correct-order--guide-text {
  font-size: 14px;
}

.correct-order--show-correct {
  color: #348EF6;
  font-size: 14px;
  line-height: 18px;
  font-weight: bold;
  cursor: pointer;
  text-decoration: underline;

  &.white {
    color: inherit; // inherit from body if directly set, else use color above #4B4B4B
  }
}

.correct-order--indices {
  width: 24px;
}

.correct-order--items {
  width: calc(100% - 24px);
  padding: 0;
  margin: 0;
}

.correct-order--index {
  height: $correct-order-item-height;
  font-weight: bold;
  font-size: 14px;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  margin-bottom: 8px;
}
.correct-order--item {
  height: $correct-order-item-height;
  display: flex;
  position: relative;
  align-items: center;
  justify-content: flex-start;
  border-width: 1px;
  border-radius: 4px;
  border-style: solid;
  margin-bottom: 8px;
  padding-left: 12px;
  &.choosen-item {
    transition: box-shadow 0.2s ease;
     &.is-dragging-dark {
      box-shadow: 0 0 10px 5px rgba(255, 255, 255, 0.05);
    }

    &.is-dragging-light {
      box-shadow: 0 0 10px 5px rgba(0, 0, 0, 0.05);

    }
  }
  &:not(.draggable) {
    cursor: not-allowed;
  }

}

.correct-order--item-text {
  width: calc(100% - 40px);
  text-align: left;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2; /* number of lines to show */
  -webkit-box-orient: vertical;
  word-break: break-all; /* wrap the url content */
}

.correct-order--drag-icon {
  margin-left: auto;
  width: 40px;
  padding-right: 8px;
  opacity: 0.6;
}

.correct-order--drag-handler {
  position: absolute;
  right: 0;
  top: 0;
  height: 100%;
  width: 80px;
  cursor: move;
  @media screen and (min-width: 640px) {
    width: 100%;
  }
}

.correct-order-select-box {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: #00000050;
  -webkit-box-sizing: border-box;
  box-sizing: border-box;
  z-index: 999;
  padding: 0 10px;
  padding-top: 140px;

  @media (max-width: 400px) {
    padding-left: 0;
    padding-right: 0;
  }
}

.overlay-box {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 1;
}

.aha-option__icon {
  background-color: #fff;
  border: 1px solid #fff;
  border-radius: 10px;
  margin-left: auto;
  margin-right: 12px;
}

.correct-order--transition-group {
  padding: 0;
  margin: 0;
}

// for transition group
.flip-list-move {
  transition: transform 1s;
}
</style>
